import React, { useState, useCallback } from 'react';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';


function CmsElementAccordion(props) {
  const {
    index,
    children,
    showElementSettings,
    editMode,
  } = props;

  // --- Local State ---
  const [shown, setShown] = useState(false);
  const [shownAccordionIndex, setShownAccordionIndex] = useState(null);

  // --- Abstract Logic Hook ---
  // Provides all helper functions previously found in the AbstractCmsElement base class
  const {
    getMainId,
    getCssClasses,
    getElementStyle,
    getBackgroundStyle,
    getParamValue,
    getParallaxData,
    getCustomId,
    getAppendHtml,
  } = useAbstractCmsElement(props);

  // --- dnd-kit Setup ---
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: index });

  const dndStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 1000 : 1,
  };

  // --- Handlers ---
  const handleShown = useCallback(() => setShown(true), []);

  const handleShowAccordion = useCallback((idx) => {
    setShownAccordionIndex(idx);
  }, []);

  const handleShowSettings = (e) => {
    if (showElementSettings) {
      showElementSettings(e, index);
    }
  };

  // --- UI Variables ---
  const shownClass = shown ? 'elem-shown ' : 'elem-hidden ';
  const mainColorClass = `text-${getParamValue('mainColor', 'master')} `;
  const parallaxData = getParallaxData();

  const innerContent = (
    <div
      id={getMainId()}
      className={`ms-accordion ${shownClass}${mainColorClass}${getCssClasses()}`}
      style={{
        ...getBackgroundStyle(),
        /*
           When in edit mode, we disable pointer events for the content.
           This allows the dnd-kit listeners on the parent div to catch
           mousedown events immediately for dragging.
        */
        pointerEvents: editMode ? 'none' : 'auto',
        userSelect: editMode ? 'none' : 'auto',
      }}
    >
      {getElementStyle()}

      <h2 className="p-l-20">{getParamValue('title')}</h2>

      {children ? (
        React.Children.map(children, (child, i) => {
          // Pass accordion logic to child elements
          return React.cloneElement(child, {
            accordionIndex: i,
            shownAccordionIndex,
            show: handleShowAccordion,
          });
        })
      ) : (
        <div className="p-t-15 p-b-15 text-center">
          <i className="fa fa-sort fa-4x" />
        </div>
      )}

      {getAppendHtml()}
    </div>
  );

  return (
    <div
      ref={setNodeRef}
      style={dndStyle}
      {...attributes}
      {...listeners}
      /*
         In editMode, we handle the click here because the inner content
         has pointerEvents: none. dnd-kit automatically suppresses the
         click event if a drag operation was performed.
      */
      onClick={editMode ? handleShowSettings : undefined}
    >
      <CustomScrollTrigger className="cms-overflow-fix"
        onEnter={handleShown}
        {...getCustomId()}
      >
        {parallaxData ? (
          <Parallax {...parallaxData}>{innerContent}</Parallax>
        ) : (
          innerContent
        )}
      </CustomScrollTrigger>

      {/* Visual drag indicator overlay */}
      {editMode && (
        <div
          className="drag-handle-indicator"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: isDragging ? '2px solid #007bff' : '1px dashed transparent',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}

export default CmsElementAccordion;
